/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { SaleService } from '../services/sale-service';
import { salesKeys } from './sales-keys';
import type { SaleDocumentCategory, SaleSettings } from '../types/sale.types';

export interface SaleSettingsViewModel {
  isLoading: boolean;
  isError: boolean;
  /** Null while loading, and until the first read resolves. */
  settings: SaleSettings | null;
  /** Null when the agency never chose one — not the same as 0. */
  defaultCommissionPercentage: number | null;
  documentCategories: SaleDocumentCategory[];
  setDefaultCommissionPercentage: (percentage: number | null) => Promise<void>;
  addDocumentCategory: (label: string) => Promise<void>;
  renameDocumentCategory: (categoryId: string, label: string) => Promise<void>;
}

/**
 * The agency's sale settings — what a new sale's commission starts from,
 * and what its documents are filed under.
 *
 * One query for both: the backend keeps them in a single document, so the
 * screen reads them in one call and every write invalidates the same key.
 *
 * @param agencyId - The owning agency.
 * @returns The view-model: the settings, load state, and the three writes.
 */
export function useSaleSettings(agencyId: string): SaleSettingsViewModel {
  const queryClient = useQueryClient();
  const enabled = Boolean(agencyId);

  const settingsQuery = useQuery({
    queryKey: salesKeys.settings(agencyId),
    queryFn: () => SaleService.readSettings(agencyId),
    enabled,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: salesKeys.settings(agencyId) });

  const percentageMutation = useMutation({
    mutationFn: (percentage: number | null) =>
      SaleService.updateSettings(agencyId, { default_commission_percentage: percentage }),
    onSuccess: invalidate,
  });

  const addMutation = useMutation({
    mutationFn: (label: string) => SaleService.addDocumentCategory(agencyId, { label }),
    onSuccess: invalidate,
  });

  const renameMutation = useMutation({
    mutationFn: (args: { categoryId: string; label: string }) =>
      SaleService.updateDocumentCategory(agencyId, args.categoryId, { label: args.label }),
    onSuccess: invalidate,
  });

  const settings = settingsQuery.data ?? null;

  return {
    isLoading: settingsQuery.isLoading,
    isError: settingsQuery.isError,
    settings,
    defaultCommissionPercentage: settings?.default_commission_percentage ?? null,
    documentCategories: settings?.document_categories ?? [],
    setDefaultCommissionPercentage: async (percentage) => {
      await percentageMutation.mutateAsync(percentage);
    },
    addDocumentCategory: async (label) => {
      await addMutation.mutateAsync(label);
    },
    renameDocumentCategory: async (categoryId, label) => {
      await renameMutation.mutateAsync({ categoryId, label });
    },
  };
}
